import React from 'react';
import { useUnit } from 'effector-react';
import { $isAuthenticated } from '../store/auth';
import { Navigate } from 'react-router-dom';
import LoginForm from '../components/LoginForm';

import { Box, Button, Paper, useTheme } from '@mui/material';
import DarkModeRounded from '@mui/icons-material/DarkModeRounded';
import LightModeRounded from '@mui/icons-material/LightModeRounded';
import loginIllustration from '../assets/images/login-illustration.png';
import Footer from '../components/Footer';
import { useAppTheme } from '../theme/AppThemeProvider';

const LoginPage = () => {
  const isAuthenticated = useUnit($isAuthenticated);
  const theme = useTheme();
  const { themeId, setThemeId } = useAppTheme();

  const handleThemeToggle = (event) => {
    const nextTheme = themeId === 'light' ? 'dark' : 'light';
    const root = document.documentElement;
    const rect = event.currentTarget.getBoundingClientRect();
    root.style.setProperty('--theme-x', `${rect.left + rect.width / 2}px`);
    root.style.setProperty('--theme-y', `${rect.top + rect.height / 2}px`);

    if (typeof document.startViewTransition === 'function') {
      document.startViewTransition(() => setThemeId(nextTheme));
    } else {
      setThemeId(nextTheme);
    }
  };

  if (isAuthenticated) {
    return <Navigate to="/home" replace />;
  }

  return (
    <Box
      className="login-page"
      sx={{
        minHeight: '100svh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 4,
        backgroundColor: 'var(--app-bg)',
        backgroundImage: 'var(--app-gradient)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          width: { xs: 260, sm: 320, md: 380 },
          height: { xs: 260, sm: 320, md: 380 },
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(37, 99, 235, 0.18) 0%, rgba(37, 99, 235, 0) 70%)',
          top: { xs: -80, sm: -100 },
          left: { xs: -120, sm: -160 },
          filter: 'blur(6px)',
          opacity: 0.7,
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          width: { xs: 240, sm: 300, md: 360 },
          height: { xs: 240, sm: 300, md: 360 },
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(14, 165, 164, 0.16) 0%, rgba(14, 165, 164, 0) 70%)',
          bottom: { xs: -120, sm: -140 },
          right: { xs: -120, sm: -160 },
          filter: 'blur(6px)',
          opacity: 0.65,
          pointerEvents: 'none',
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: { xs: 16, sm: 24 },
          right: { xs: 16, sm: 24 },
          zIndex: 3,
        }}
      >
        <Button
          type="button"
          onClick={handleThemeToggle}
          className="glass-sheen"
          startIcon={themeId === 'light' ? <LightModeRounded /> : <DarkModeRounded />}
          aria-label={`Тема: ${themeId === 'light' ? 'светлая' : 'тёмная'}`}
          title={`Тема: ${themeId === 'light' ? 'светлая' : 'тёмная'}`}
          sx={{
            textTransform: 'none',
            borderRadius: '999px',
            px: { xs: 2, sm: 2.5 },
            py: { xs: 0.8, sm: 1 },
            fontWeight: 600,
            fontSize: { xs: '13px', sm: '14px' },
            color: 'text.primary',
            backgroundColor:
              theme.palette.mode === 'dark'
                ? 'rgba(15, 23, 42, 0.7)'
                : 'rgba(255, 255, 255, 0.78)',
            border:
              theme.palette.mode === 'dark'
                ? '1px solid rgba(148, 163, 184, 0.22)'
                : '1px solid rgba(15, 23, 42, 0.12)',
            boxShadow:
              theme.palette.mode === 'dark'
                ? '0 12px 28px rgba(2, 6, 23, 0.45)'
                : '0 12px 24px rgba(15, 23, 42, 0.14)',
            backdropFilter: 'blur(14px)',
            minHeight: 40,
            '& .MuiButton-startIcon': {
              marginRight: 1,
              '& svg': {
                fontSize: 20,
              },
            },
            '&:hover': {
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? 'rgba(30, 41, 59, 0.78)'
                  : 'rgba(255, 255, 255, 0.92)',
              boxShadow:
                theme.palette.mode === 'dark'
                  ? '0 16px 30px rgba(2, 6, 23, 0.55)'
                  : '0 16px 28px rgba(15, 23, 42, 0.18)',
            },
            '&:focus-visible': {
              outline: '2px solid rgba(37, 99, 235, 0.55)',
              outlineOffset: '2px',
            },
          }}
        >
          Тема: {themeId === 'light' ? 'светлая' : 'тёмная'}
        </Button>
      </Box>

      <Paper
        elevation={3}
        sx={{
          p: { xs: 3, sm: 4, md: 5, lg: 6, xl: 8 },
          px: { xs: 3, sm: 6, md: 8, lg: 10, xl: 13 },
          width: '100%',
          maxWidth: { xs: '100%', sm: 560, md: 600, lg: 680, xl: 700 },
          borderRadius: '28px',
          boxShadow: (theme) =>
            theme.palette.mode === 'dark'
              ? '0 30px 60px rgba(2, 6, 23, 0.55)'
              : '0 24px 50px rgba(15, 23, 42, 0.12)',
          backgroundColor: (theme) =>
            theme.palette.mode === 'dark'
              ? 'rgba(15, 23, 42, 0.72)'
              : 'rgba(255, 255, 255, 0.92)',
          border: (theme) =>
            theme.palette.mode === 'dark'
              ? '1px solid rgba(148, 163, 184, 0.16)'
              : '1px solid rgba(15, 23, 42, 0.08)',
          backdropFilter: 'blur(14px)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <LoginForm />
      </Paper>

      {/* Иллюстрация поверх формы, смещена вправо и по центру */}
      <Box
        component="img"
        src={loginIllustration}
        alt=""
        aria-hidden
        sx={{
          position: 'absolute',
          top: '50%',
          right: { xs: 0, sm: 250, md: 320 },
          transform: 'translate(30%, -50%)',
          width: { xs: 0, sm: 220, md: 260, lg: 300, xl: 360 },
          display: { xs: 'none', lg: 'block' },
          zIndex: 2,
          pointerEvents: 'none',
          filter: 'drop-shadow(0 10px 28px rgba(0,0,0,0.18))',
          opacity: 0.95,
        }}
      />
      <Footer disableLinks />
    </Box>
  );
};

export default LoginPage;
